import { useEffect, useState } from 'react';
import { createReceivingItem, updateReceivingItem } from '../../services/receivingService';
import {
  finalizePurchaseOrderForBalancing,
  reopenPurchaseOrderReceivingForBalancing,
  updatePurchaseOrderProgress,
} from '../../services/purchaseOrderService';
import {
  createEmptyProgressValues,
  getInitialProgressValues,
} from './purchaseOrderHelpers';

function usePurchaseOrderProgress({ purchaseOrders = [], setPurchaseOrders, setSuccessMessage }) {
  const [progressingPurchaseOrder, setProgressingPurchaseOrder] = useState(null);
  const [progressValues, setProgressValues] = useState(createEmptyProgressValues);
  const [isProgressSaving, setIsProgressSaving] = useState(false);
  const [isReceivingSaving, setIsReceivingSaving] = useState(false);
  const [progressError, setProgressError] = useState('');
  const [receivingEditValues, setReceivingEditValues] = useState([]);
  const [initialReceivingEditValues, setInitialReceivingEditValues] = useState([]);

  function openProgressModal(purchaseOrder) {
    const nextReceivingEditValues = getInitialReceivingEditValues(purchaseOrder);

    setProgressingPurchaseOrder(purchaseOrder);
    setProgressValues(getInitialProgressValues(purchaseOrder));
    setReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
    setInitialReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
    setProgressError('');
    setSuccessMessage('');
  }

  useEffect(() => {
    if (!progressingPurchaseOrder) {
      return;
    }

    const latestPurchaseOrder = purchaseOrders.find(
      (purchaseOrder) => Number(purchaseOrder.id) === Number(progressingPurchaseOrder.id)
    );

    if (
      !latestPurchaseOrder ||
      !hasProgressSnapshotChanged(progressingPurchaseOrder, latestPurchaseOrder)
    ) {
      return;
    }

    const nextReceivingEditValues = getInitialReceivingEditValues(latestPurchaseOrder);

    setProgressingPurchaseOrder(latestPurchaseOrder);
    setProgressValues(getInitialProgressValues(latestPurchaseOrder));
    setReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
    setInitialReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
    setProgressError('');
  }, [progressingPurchaseOrder, purchaseOrders]);

  function closeProgressModal() {
    setProgressingPurchaseOrder(null);
    setProgressValues(createEmptyProgressValues());
    setReceivingEditValues([]);
    setInitialReceivingEditValues([]);
    setProgressError('');
  }

  function handleProgressChange(event) {
    const { name, value } = event.target;

    setProgressValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setProgressError('');
  }

  function resetProgressValues() {
    if (!progressingPurchaseOrder) {
      return;
    }

    setProgressValues(getInitialProgressValues(progressingPurchaseOrder));
    setProgressError('');
  }

  function resetReceivingEditValues() {
    if (!progressingPurchaseOrder) {
      return;
    }

    setReceivingEditValues(cloneReceivingEditValues(initialReceivingEditValues));
    setProgressError('');
  }

  function handleReceivingEditChange(itemId, fieldName, value) {
    setReceivingEditValues((currentItems) =>
      currentItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [fieldName]: value,
            }
          : item
      )
    );
    setProgressError('');
  }

  async function handleReceivingEditSubmit() {
    if (!progressingPurchaseOrder) {
      return false;
    }

    const normalizedReceivingEditValues = normalizeGrossReceivingItems(receivingEditValues);
    const nextErrors = validateReceivingEditValues(
      normalizedReceivingEditValues,
      progressingPurchaseOrder.stockUnit
    );

    if (nextErrors.length) {
      setProgressError(nextErrors[0]);
      return false;
    }

    setIsReceivingSaving(true);
    setProgressError('');

    try {
      const updatedReceivingItems = [];
      const receivingItemsToSave = normalizedReceivingEditValues
        .filter(
          (item) =>
            !(
              item.isNew &&
              item.category === 'Reject' &&
              Number(item.quantity || 0) <= 0 &&
              Number(item.quantityKg || item.rawNetKg || 0) <= 0
            )
        )
        .sort((firstItem, secondItem) => getQuantityDelta(firstItem) - getQuantityDelta(secondItem));

      for (const item of receivingItemsToSave) {
        const isRejectNetKiloItem =
          item.quantityUnit === 'sacks' &&
          String(item.category || '').trim() === 'Reject';
        const receivingPayload = {
          purchaseOrderId: progressingPurchaseOrder.id,
          category: item.category,
          moistureContent: item.moistureContent.trim(),
          quantity: isRejectNetKiloItem ? 0 : Number(item.quantity),
          quantityUnit: item.quantityUnit,
          quantityKg: getReceivingItemCostQuantityKg(item),
          rawNetKg: normalizeOptionalNumber(item.rawNetKg),
          mcPercent: normalizeOptionalNumber(item.mcPercent),
          mcDeductionKg: normalizeOptionalNumber(item.mcDeductionKg),
          adjustedNetKg: normalizeOptionalNumber(item.adjustedNetKg),
          unitCost: Number(item.unitCost),
          remarks: item.remarks.trim(),
          image: item.image || '',
          receivedAt: item.receivedAt || new Date().toISOString(),
        };
        const updatedReceivingItem = item.isNew
          ? await createReceivingItem(receivingPayload)
          : await updateReceivingItem(item.id, receivingPayload);

        updatedReceivingItems.push(updatedReceivingItem);
      }

      const nextReceivingItems = mergeReceivingItems(
        progressingPurchaseOrder.receivingItems,
        updatedReceivingItems
      );
      const nextActualPrice = getReceivingUnitCost(
        nextReceivingItems,
        progressingPurchaseOrder.actualPrice ?? progressingPurchaseOrder.price
      );
      const updatedPurchaseOrder = {
        ...progressingPurchaseOrder,
        actualPrice: nextActualPrice,
        receivingItems: nextReceivingItems,
      };

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((purchaseOrder) =>
          purchaseOrder.id === updatedPurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
        )
      );
      setProgressingPurchaseOrder(updatedPurchaseOrder);
      const nextReceivingEditValues = getInitialReceivingEditValues(updatedPurchaseOrder);

      setReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
      setInitialReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
      setProgressValues((currentValues) => ({
        ...currentValues,
        actualPrice: String(nextActualPrice),
      }));
      setSuccessMessage('Receiving details updated successfully.');
      return updatedPurchaseOrder;
    } catch (error) {
      setProgressError(error.message);
      return false;
    } finally {
      setIsReceivingSaving(false);
    }
  }

  async function handleProgressSubmit(_event, purchaseOrderOverride = null) {
    const activePurchaseOrder = purchaseOrderOverride || progressingPurchaseOrder;

    if (!activePurchaseOrder) {
      return false;
    }

    const normalizedReceivedQuantity = Number(progressValues.receivedQuantity);

    if (!progressValues.receivedQuantity || normalizedReceivedQuantity <= 0) {
      setProgressError('Enter the quantity that was actually received.');
      return false;
    }

    if (
      activePurchaseOrder.stockUnit === 'sacks' &&
      !Number.isInteger(normalizedReceivedQuantity)
    ) {
      setProgressError('Sacks quantity must be a whole number.');
      return false;
    }

    const progressActualPrice =
      activePurchaseOrder.status === 'Received'
        ? activePurchaseOrder.actualPrice ?? progressValues.actualPrice
        : progressValues.actualPrice;
    const normalizedActualPrice = Number(progressActualPrice);

    if (
      progressActualPrice === '' ||
      Number.isNaN(normalizedActualPrice) ||
      normalizedActualPrice <= 0
    ) {
      setProgressError('Enter a valid actual price.');
      return false;
    }

    const normalizedWarehouseLocation = String(progressValues.warehouseLocation || '').trim();

    if (!String(progressValues.lansaName || '').trim()) {
      setProgressError('Lansa name is required before receiving can proceed.');
      return false;
    }

    if (!normalizedWarehouseLocation) {
      setProgressError('Warehouse location is required before receiving can proceed.');
      return false;
    }

    setIsProgressSaving(true);
    setProgressError('');

    try {
      const nextStatus =
        activePurchaseOrder.status === 'Received' ? 'Completed' : progressValues.status;
      const updatedPurchaseOrder = await updatePurchaseOrderProgress(
        activePurchaseOrder.id,
        {
          status: nextStatus,
          actualPrice: normalizedActualPrice,
          receivedQuantity: normalizedReceivedQuantity,
          lansaName: progressValues.lansaName,
          warehouseLocation: normalizedWarehouseLocation,
          deliveryDate: progressValues.deliveryDate || null,
        }
      );

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((purchaseOrder) =>
          purchaseOrder.id === activePurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
        )
      );
      setProgressingPurchaseOrder(updatedPurchaseOrder);
      setProgressValues((currentValues) => ({
        ...currentValues,
        status: updatedPurchaseOrder.status,
        actualPrice: String(
          updatedPurchaseOrder.actualPrice ?? updatedPurchaseOrder.price ?? currentValues.actualPrice
        ),
        receivedQuantity: String(
          updatedPurchaseOrder.receivedQuantity ?? currentValues.receivedQuantity
        ),
        lansaName: updatedPurchaseOrder.lansaName || '',
        warehouseLocation: updatedPurchaseOrder.warehouseLocation || '',
        deliveryDate: updatedPurchaseOrder.deliveryDate || '',
      }));
      setSuccessMessage('Purchase order progress updated successfully.');
      return true;
    } catch (error) {
      setProgressError(error.message);
      return false;
    } finally {
      setIsProgressSaving(false);
    }
  }

  async function handleFinalizeForBalancing(purchaseOrderOverride = null) {
    const activePurchaseOrder = purchaseOrderOverride || progressingPurchaseOrder;

    if (!activePurchaseOrder) {
      return false;
    }

    setIsProgressSaving(true);
    setProgressError('');

    try {
      const updatedPurchaseOrder = await finalizePurchaseOrderForBalancing(activePurchaseOrder.id);

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((purchaseOrder) =>
          purchaseOrder.id === updatedPurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
        )
      );
      setProgressingPurchaseOrder(updatedPurchaseOrder);
      setSuccessMessage('Receiving finalized for supplier balancing.');
      return updatedPurchaseOrder;
    } catch (error) {
      setProgressError(error.message);
      return false;
    } finally {
      setIsProgressSaving(false);
    }
  }

  async function handleReopenReceivingForBalancing() {
    if (!progressingPurchaseOrder) {
      return false;
    }

    setIsProgressSaving(true);
    setProgressError('');

    try {
      const updatedPurchaseOrder = await reopenPurchaseOrderReceivingForBalancing(
        progressingPurchaseOrder.id
      );

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((purchaseOrder) =>
          purchaseOrder.id === updatedPurchaseOrder.id ? updatedPurchaseOrder : purchaseOrder
        )
      );
      setProgressingPurchaseOrder(updatedPurchaseOrder);
      const nextReceivingEditValues = getInitialReceivingEditValues(updatedPurchaseOrder);

      setReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
      setInitialReceivingEditValues(cloneReceivingEditValues(nextReceivingEditValues));
      setSuccessMessage('Receiving details reopened for editing.');
      return updatedPurchaseOrder;
    } catch (error) {
      setProgressError(error.message);
      return false;
    } finally {
      setIsProgressSaving(false);
    }
  }

  return {
    progressingPurchaseOrder,
    progressValues,
    receivingEditValues,
    initialReceivingEditValues,
    isProgressSaving,
    isReceivingSaving,
    progressError,
    openProgressModal,
    closeProgressModal,
    handleProgressChange,
    resetProgressValues,
    resetReceivingEditValues,
    handleProgressSubmit,
    handleFinalizeForBalancing,
    handleReopenReceivingForBalancing,
    handleReceivingEditChange,
    handleReceivingEditSubmit,
  };
}

function getInitialReceivingEditValues(purchaseOrder) {
  if (!Array.isArray(purchaseOrder.receivingItems)) {
    return [];
  }

  const receiptNetKg = getReceiptNumericValue(
    parseReceiptDetails(
      getReceiptDetailsFromRemarks(
        purchaseOrder.receivingItems.map((item) => item.remarks).find(Boolean)
      )
    ),
    'Net KG'
  );
  const totalSacks = purchaseOrder.receivingItems.reduce(
    (total, item) =>
      (item.quantityUnit || purchaseOrder.stockUnit) === 'sacks' &&
      String(item.category || '').trim() !== 'Reject'
        ? total + Number(item.quantity || 0)
        : total,
    0
  );
  const storedTotalNetKg = purchaseOrder.receivingItems.reduce(
    (total, item) =>
      (item.quantityUnit || purchaseOrder.stockUnit) === 'sacks' &&
      String(item.category || '').trim() !== 'Reject'
        ? total + Number(item.quantityKg || 0)
        : total,
    0
  );
  const storedRawNetKg = roundKg(
    purchaseOrder.receivingItems.reduce(
      (total, item) =>
        (item.quantityUnit || purchaseOrder.stockUnit) === 'sacks' &&
        String(item.category || '').trim() !== 'Reject'
          ? total + Number(item.rawNetKg || 0)
          : total,
      0
    )
  );
  const grossKg = receiptNetKg || storedRawNetKg || storedTotalNetKg;

  const mappedItems = purchaseOrder.receivingItems.map((item) => {
    const quantityUnit = item.quantityUnit || purchaseOrder.stockUnit || 'kg';
    const isRejectItem = String(item.category || '').trim() === 'Reject';
    const quantity = Number(item.quantity ?? item.quantityKg ?? 0);
    const storedQuantityKg = Number(item.quantityKg ?? 0);
    const quantityKg =
      quantityUnit === 'sacks' && !isRejectItem && grossKg > 0 && totalSacks > 0
        ? roundKg((quantity / totalSacks) * grossKg)
        : storedQuantityKg > 0
        ? storedQuantityKg
        : Number(item.quantityKg ?? item.quantity ?? 0);

    return {
      id: item.id,
      category: item.category || 'Dry',
      moistureContent: item.moistureContent || '0',
      quantity:
        typeof item.quantity === 'undefined' || item.quantity === null
          ? '0'
          : quantityUnit === 'sacks' && isRejectItem
          ? '0'
          : String(item.quantity),
      originalQuantity:
        typeof item.quantity === 'undefined' || item.quantity === null
          ? 0
          : quantityUnit === 'sacks' && isRejectItem
          ? 0
          : Number(item.quantity),
      quantityUnit,
      quantityKg: String(quantityKg || ''),
      rawNetKg: stringifyOptionalNumber(
        quantityUnit === 'sacks' && !isRejectItem && grossKg > 0 && totalSacks > 0
          ? roundKg((quantity / totalSacks) * grossKg)
          : item.rawNetKg
      ),
      mcPercent: stringifyOptionalNumber(item.mcPercent),
      mcDeductionKg: '',
      adjustedNetKg: '',
      unitCost: String(item.unitCost ?? 0),
      subtotal: Number(item.subtotal ?? quantityKg * Number(item.unitCost || 0)),
      remarks: item.remarks || '',
      image: item.image || '',
      imageUploadedAt: item.imageUploadedAt || '',
      imageExpiresAt: item.imageExpiresAt || '',
      imageRetentionDays: item.imageRetentionDays,
      imageDaysRemaining: item.imageDaysRemaining,
      receivedAt: item.receivedAt,
      productName: item.productName || 'Seaweed',
    };
  });

  if (!mappedItems.some((item) => item.category === 'Reject')) {
    const templateItem = mappedItems[0] || {};

    mappedItems.push({
      ...templateItem,
      id: `new-reject-${purchaseOrder.id}`,
      isNew: true,
      category: 'Reject',
      quantity: '0',
      originalQuantity: 0,
      quantityKg: '0',
      rawNetKg: '0',
      mcDeductionKg: '',
      adjustedNetKg: '',
      subtotal: 0,
    });
  }

  return mappedItems;
}

function validateReceivingEditValues(items, stockUnit) {
  const errors = items.reduce((currentErrors, item) => {
    if (!item.moistureContent.trim()) {
      return [...currentErrors, 'Moisture content is required.'];
    }

    const quantity = Number(item.quantity || 0);

    if (!Number.isFinite(quantity) || quantity < 0) {
      return [...currentErrors, 'Quantity must be zero or greater.'];
    }

    if (item.unitCost === '' || Number(item.unitCost) < 0) {
      return [...currentErrors, 'Unit cost must be zero or greater.'];
    }

    if (
      quantity > 0 &&
      item.category !== 'Reject' &&
      item.quantityUnit === 'sacks' &&
      Number(item.quantityKg || 0) <= 0
    ) {
      return [...currentErrors, 'Total net KG must be greater than zero.'];
    }

    return currentErrors;
  }, []);

  const totalEnteredQuantity = items.reduce(
    (total, item) => {
      const isRejectItem = String(item.category || '').trim() === 'Reject';

      return stockUnit === 'sacks' && isRejectItem
        ? total + Number(item.quantityKg || item.rawNetKg || 0)
        : total + Number(item.quantity || 0);
    },
    0
  );

  if (totalEnteredQuantity <= 0) {
    return [...errors, 'Enter a dry, wet, or reject quantity greater than 0.'];
  }

  return errors;
}

function getQuantityDelta(item) {
  return Number(item.quantity || 0) - Number(item.originalQuantity || 0);
}

function mergeReceivingItems(currentItems = [], updatedItems = []) {
  const updatedItemsById = new Map(
    updatedItems.map((item) => [Number(item.id), item])
  );
  const currentItemIds = new Set(currentItems.map((item) => Number(item.id)));
  const createdItems = updatedItems.filter((item) => !currentItemIds.has(Number(item.id)));

  return [
    ...currentItems.map((item) => updatedItemsById.get(Number(item.id)) || item),
    ...createdItems,
  ];
}

function getReceivingUnitCost(items = [], fallbackValue = 0) {
  const itemWithUnitCost = items.find((item) => {
    const numericUnitCost = Number(item.unitCost);

    return Number.isFinite(numericUnitCost) && numericUnitCost > 0;
  });

  return itemWithUnitCost ? Number(itemWithUnitCost.unitCost) : fallbackValue;
}

function getReceivingItemCostQuantityKg(item) {
  if (item.quantityUnit !== 'sacks') {
    return Number(item.quantity);
  }

  return Number(item.quantityKg || 0);
}

function normalizeGrossReceivingItems(items) {
  const totalSacks = items.reduce(
    (total, item) =>
      item.quantityUnit === 'sacks' && item.category !== 'Reject'
        ? total + Number(item.quantity || 0)
        : total,
    0
  );
  const firstReceiptDetails = getReceiptDetailsFromRemarks(
    items.map((item) => item.remarks).find(Boolean)
  );
  const receiptGrossKg = getReceiptNumericValue(parseReceiptDetails(firstReceiptDetails), 'Net KG');
  const storedRawNetKg = roundKg(
    items.reduce(
      (total, item) =>
        item.quantityUnit === 'sacks' && item.category !== 'Reject'
          ? total + Number(item.rawNetKg || 0)
          : total,
      0
    )
  );
  const storedNetKg = items.reduce(
    (total, item) =>
      item.quantityUnit === 'sacks' && item.category !== 'Reject'
        ? total + Number(item.quantityKg || 0)
        : total,
    0
  );
  const grossKg = receiptGrossKg || storedRawNetKg || storedNetKg;
  const nextReceiptDetails =
    grossKg > 0
      ? buildGrossReceiptDetails(firstReceiptDetails, grossKg)
      : firstReceiptDetails;

  const allocatedItems = allocateGrossKgByQuantity(items, grossKg);

  return items.map((item) => {
    if (item.quantityUnit !== 'sacks' || item.category === 'Reject' || grossKg <= 0 || totalSacks <= 0) {
      return item;
    }

    const allocatedGrossKg = allocatedItems.get(item.id);

    return {
      ...item,
      quantityKg: allocatedGrossKg === '' ? '' : String(allocatedGrossKg),
      rawNetKg: allocatedGrossKg === '' ? '' : String(allocatedGrossKg),
      mcDeductionKg: '',
      adjustedNetKg: '',
      remarks: mergeRemarksAndReceiptDetails(
        getRemarksOnly(item.remarks),
        nextReceiptDetails
      ),
    };
  });
}

function normalizeOptionalNumber(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
}

function stringifyOptionalNumber(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '';
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) && numericValue > 0 ? String(numericValue) : '';
}

function buildGrossReceiptDetails(receiptDetails, grossKg) {
  const cleanedDetails = removeReceiptDetailLines(receiptDetails, [
    'MC Deduction KG',
    'Adjusted Net KG',
  ]);

  return upsertReceiptDetailLine(cleanedDetails, 'Net KG', `${formatDecimal(grossKg)} kg`);
}

function removeReceiptDetailLines(receiptDetails, labelsToRemove) {
  const normalizedLabels = labelsToRemove.map((label) => label.toLowerCase());

  return String(receiptDetails || '')
    .split('\n')
    .filter((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return true;
      }

      return !normalizedLabels.includes(line.slice(0, separatorIndex).trim().toLowerCase());
    })
    .join('\n')
    .trim();
}

function upsertReceiptDetailLine(receiptDetails, label, value) {
  const lines = String(receiptDetails || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const lineIndex = lines.findIndex((line) => {
    const separatorIndex = line.indexOf(':');

    return (
      separatorIndex !== -1 &&
      line.slice(0, separatorIndex).trim().toLowerCase() === label.toLowerCase()
    );
  });
  const nextLine = `${label}: ${value}`;

  if (lineIndex === -1) {
    return [...lines, nextLine].join('\n');
  }

  return lines.map((line, index) => (index === lineIndex ? nextLine : line)).join('\n');
}

function mergeRemarksAndReceiptDetails(remarks, receiptDetails) {
  return [String(remarks || '').trim(), String(receiptDetails || '').trim()]
    .filter(Boolean)
    .join('\n\n');
}

function getRemarksOnly(value) {
  const lines = String(value || '').split('\n');
  const firstReceiptLineIndex = lines.findIndex(isReceiptDetailLine);

  return firstReceiptLineIndex === -1
    ? String(value || '').trim()
    : lines.slice(0, firstReceiptLineIndex).join('\n').trim();
}

function roundKg(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function allocateGrossKgByQuantity(items = [], grossKg = 0) {
  const allocatableItems = items.filter(
    (item) =>
      item.quantityUnit === 'sacks' &&
      item.category !== 'Reject' &&
      Number(item.quantity || 0) > 0
  );
  const totalQuantity = allocatableItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
  const normalizedGrossKg = Number(grossKg || 0);

  if (normalizedGrossKg <= 0 || totalQuantity <= 0) {
    return new Map(allocatableItems.map((item) => [item.id, '']));
  }

  let allocatedTotal = 0;

  return allocatableItems.reduce((allocations, item, index) => {
    const isLastItem = index === allocatableItems.length - 1;
    const allocatedKg = isLastItem
      ? roundKg(normalizedGrossKg - allocatedTotal)
      : roundKg((Number(item.quantity || 0) / totalQuantity) * normalizedGrossKg);

    allocatedTotal = roundKg(allocatedTotal + allocatedKg);
    allocations.set(item.id, allocatedKg);

    return allocations;
  }, new Map());
}

function formatDecimal(value) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function cloneReceivingEditValues(items = []) {
  return items.map((item) => ({ ...item }));
}

function hasProgressSnapshotChanged(currentPurchaseOrder, latestPurchaseOrder) {
  return (
    getProgressSnapshot(currentPurchaseOrder) !== getProgressSnapshot(latestPurchaseOrder)
  );
}

function getProgressSnapshot(purchaseOrder) {
  return JSON.stringify({
    status: purchaseOrder?.status || '',
    actualPrice: purchaseOrder?.actualPrice ?? null,
    receivedQuantity: purchaseOrder?.receivedQuantity ?? null,
    lansaName: purchaseOrder?.lansaName || '',
    warehouseLocation: purchaseOrder?.warehouseLocation || '',
    balancingReadyAt: purchaseOrder?.balancingReadyAt || '',
    receivingItems: getReceivingItemsSnapshot(purchaseOrder?.receivingItems),
  });
}

function getReceivingItemsSnapshot(receivingItems = []) {
  return (Array.isArray(receivingItems) ? receivingItems : []).map((item) => ({
    id: item.id,
    category: item.category,
    quantity: item.quantity,
    quantityKg: item.quantityKg,
    rawNetKg: item.rawNetKg,
    mcPercent: item.mcPercent,
    unitCost: item.unitCost,
    receivedAt: item.receivedAt,
  }));
}

function getReceiptDetailsFromRemarks(value) {
  const lines = String(value || '').split('\n');
  const firstReceiptLineIndex = lines.findIndex(isReceiptDetailLine);

  return firstReceiptLineIndex === -1 ? '' : lines.slice(firstReceiptLineIndex).join('\n').trim();
}

function parseReceiptDetails(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return null;
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);
}

function getReceiptNumericValue(fields, label) {
  const field = fields.find(
    (currentField) => currentField.label.toLowerCase() === label.toLowerCase()
  );

  if (!field) {
    return 0;
  }

  return Number(String(field.value || '').replace(/[^\d.]/g, '')) || 0;
}

function isReceiptDetailLine(line) {
  return /^(P\.O\. Code|Ticket No\.|Customer|Commodity|Plate #|Driver|Weigher|Inbound Wt kg|In Bound Wt kg|Inbound Date|In Bound Date|Outbound Wt kg|Outbound|Outbound Date|Gross KG|Tare KG|Net KG|MC Deduction KG|Adjusted Net KG):/.test(
    line.trim()
  );
}

export default usePurchaseOrderProgress;
